import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReservationForm from '../components/ReservationForm';
import { getRestaurantById } from '../services/restaurantService';
import './ReservationWidget.css';

/**
 * ReservationWidget - Standalone widget page for embedding in external websites via iframe
 * This page contains only the reservation form without navbar, footer, or any other UI elements
 * Query params:
 * - restaurantId: The ID of the restaurant (required)
 */
const ReservationWidget = () => {
  const [searchParams] = useSearchParams();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isEventDate, setIsEventDate] = useState(false);
  const [eventHouseRules, setEventHouseRules] = useState('');

  const restaurantId = searchParams.get('restaurantId');

  useEffect(() => {
    if (!restaurantId) {
      setError('Restaurant ID is required');
      setLoading(false);
      return;
    }

    const loadRestaurant = async () => {
      try {
        setLoading(true);
        const data = await getRestaurantById(restaurantId);
        setRestaurant(data);
        setError(null);
      } catch (err) {
        console.error('Error loading restaurant:', err);
        setError('Failed to load restaurant information');
      } finally {
        setLoading(false);
      }
    };

    loadRestaurant();
  }, [restaurantId]);

  if (!restaurantId) {
    return (
      <div className="widget-error-container">
        <p className="widget-error-message">
          Missing restaurant ID. Please provide a restaurantId in the URL query parameter.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="widget-loading-container">
        <div className="widget-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="widget-error-container">
        <p className="widget-error-message">{error}</p>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="widget-error-container">
        <p className="widget-error-message">Restaurant not found</p>
      </div>
    );
  }

  // Build custom color styles from restaurant settings
  const customColorStyles = {
    '--primary-purple': restaurant.widgetPrimaryColor || '#1A1A1A',
    '--secondary-purple': restaurant.widgetSecondaryColor || '#2D2D2D',
    '--accent-lime': restaurant.widgetAccentColor || '#BFFF00',
    '--text-dark': restaurant.widgetTextColor || '#1a1a1a',
    '--white': restaurant.widgetBackgroundColor || '#ffffff',
    // Derived colors for consistency
    '--light-purple': restaurant.widgetSecondaryColor || '#333333',
    '--bg-light': restaurant.widgetBackgroundColor || '#f9f9f9',
  };

  return (
    <div className="reservation-widget-wrapper" style={customColorStyles}>
      {/* Restaurant Image Header */}
      {restaurant.imageUrl && (
        <div className="widget-restaurant-header">
          <div className="widget-restaurant-image-wrapper">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${restaurant.imageUrl}`}
              alt={restaurant.name}
              className="widget-restaurant-image"
            />
            {(restaurant.location || (restaurant.phoneNumbers && restaurant.phoneNumbers.length > 0)) && (
              <div className="widget-restaurant-overlay">
                {restaurant.location && (
                  <p className="widget-restaurant-location">
                    <i className="bi bi-geo-alt-fill"></i> {restaurant.location}
                  </p>
                )}
                {restaurant.phoneNumbers && restaurant.phoneNumbers.length > 0 && (
                  <div className="widget-restaurant-contacts">
                    {restaurant.phoneNumbers.map((phone, index) => (
                      <a key={index} href={`tel:${phone}`} className="widget-restaurant-phone">
                        <i className="bi bi-telephone-fill"></i> {phone}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Widget Note */}
      {restaurant.widgetNote && restaurant.widgetNote.replace(/<[^>]*>/g, '').trim() && !showSuccess && !isEventDate && (
        <div className="widget-note-container">
          <div
            className="widget-note-text"
            dangerouslySetInnerHTML={{ __html: restaurant.widgetNote }}
          />
        </div>
      )}
      {/* Event House Rules - shown only on event dates */}
      {eventHouseRules && eventHouseRules.replace(/<[^>]*>/g, '').trim() && !showSuccess && (
        <div className="widget-note-container">
          <div
            className="widget-note-text"
            dangerouslySetInnerHTML={{ __html: eventHouseRules }}
          />
        </div>
      )}
      <ReservationForm
        restaurantId={restaurantId}
        restaurantName={restaurant.name}
        restaurant={restaurant}
        isWidget={true}
        onSuccessChange={setShowSuccess}
        onEventDateChange={setIsEventDate}
        onEventHouseRulesChange={setEventHouseRules}
      />
    </div>
  );
};

export default ReservationWidget;
